import React, { useState, useEffect } from "react"
import { Box } from "@mui/system"
import PropTypes from "prop-types"
import { Grid } from "@mui/material"
import { useRatingEmissaoTable } from "../../hooks/table-emission-hooks/use-last-rating-emission"
import { usePrecosProcessados } from "../../hooks/table-emission-hooks/use-precos-processados"
import { useRatingDevedorTable } from "../../hooks/table-emission-hooks/use-last-debtor-table"
import { useIndiceLiquidezTable } from "../../hooks/table-emission-hooks/use-last-index-liquidity"
import { useUltimoNegocioTable } from "../../hooks/table-emission-hooks/use-last-business"
import { useUltimoEventoCreditoEmissao } from "../../hooks/table-emission-hooks/use-last-event-credit-emission"
import { useUltimoEventoCreditoDevedor } from "../../hooks/table-emission-hooks/use-last-event-debtor"
import SummarySection from "src/v2/components/SummarySection/summary-section"
import { getSummarySections } from "./constants/summary-sections"
import { getEmissionSummaryModalConfig } from "./configs/emission-modal-config"
import EmissionDetailsModal from "../emission-details-modal/emission-details-modal"
import { getEmissionDetailsModalConfig } from "./configs/get-emission-details-modal-config"

export const EmissionSummarySections = ({
    emissaoDatalakeService,
    characterData,
    contraparteData,
    garantiasData,
    offerData,
    fatosRelevantesData,
    admin,
}) => {
    const data = characterData || {}
    const cetip = data?.CETIP || ""
    const isin = data?.ISIN || ""

    const ofertaData = offerData || {}
    const fatosData = Array.isArray(fatosRelevantesData)
        ? fatosRelevantesData
        : []

    const cpfCnpj =
        contraparteData?.CPFCNPJ_Devedor ||
        data?.CPFCNPJ_Devedor ||
        ""

    const [modalIsVisible, setModalIsVisible] = useState(false)
    const [modalType, setModalType] = useState("")

    const [detailsModalVisible, setDetailsModalVisible] = useState(false)
    const [detailsModalType, setDetailsModalType] = useState("")

    const { state: stateRating, actions: ratingActions } = useRatingEmissaoTable(
        emissaoDatalakeService,
        cetip,
        data
    )

    const {
        state: statePriceProcess,
        actions: priceProcessActions,
    } = usePrecosProcessados(
        emissaoDatalakeService,
        cetip,
        data
    )

    const {
        state: stateIndexLiquidity,
        actions: indexLiquidityActions,
    } = useIndiceLiquidezTable(
        emissaoDatalakeService,
        cetip,
        data
    )

    const {
        state: lastBusinessState,
        actions: lastBusinessActions,
    } = useUltimoNegocioTable(
        emissaoDatalakeService,
        cetip,
        data
    )

    const {
        state: stateDebtorEmission,
        actions: debtorEmissionActions,
    } = useRatingDevedorTable(emissaoDatalakeService, cpfCnpj, contraparteData)

    const {
        state: lastEventCreditEmissionState,
        actions: lastEventCreditEmissionActions,
    } = useUltimoEventoCreditoEmissao(
        emissaoDatalakeService,
        cetip,
        data
    )

    const {
        state: lastEventCreditDebtorState,
        actions: lastEventCreditDebtorActions,
    } = useUltimoEventoCreditoDevedor(
        emissaoDatalakeService,
        cpfCnpj,
        contraparteData,
        cetip
    )

    useEffect(() => {
        if (!cetip) return

        void ratingActions.loadTableRows()
        void lastEventCreditEmissionActions.loadTableRows()
        void priceProcessActions.loadTableRows()
        void indexLiquidityActions.loadTableRows()
        void lastBusinessActions.loadTableRows()
    }, [cetip])

    useEffect(() => {
        if (!cpfCnpj) return

        void debtorEmissionActions.loadTableRows()
        void lastEventCreditDebtorActions.loadTableRows()
    }, [cpfCnpj, cetip])

    const modalConfig = getEmissionSummaryModalConfig({
        admin,
        characterData: data,
        contraparteData,
        cetip,
        isin,
        cpfCnpj,
        stateRating,
        ratingActions,
        stateDebtorEmission,
        debtorEmissionActions,
        lastEventCreditEmissionState,
        lastEventCreditEmissionActions,
        lastEventCreditDebtorState,
        lastEventCreditDebtorActions,
        stateIndexLiquidity,
        indexLiquidityActions,
        lastBusinessState,
        lastBusinessActions,
        statePriceProcess,
        priceProcessActions,
    })

    const detailsModalConfig = getEmissionDetailsModalConfig({
        characterData: data,
        garantiasData,
        fatosRelevantesData: fatosData,
        offerData: ofertaData,
    })

    const handleClickSummaryModal = (value) => {
        if (detailsModalConfig[value]) {
            setDetailsModalType(value)
            setDetailsModalVisible(true)
            return
        }

        if (!modalConfig[value]) return

        setModalType(value)
        setModalIsVisible(true)
    }

    const selectedModalConfig = modalType ? modalConfig[modalType] : null

    const selectedDetailsModalConfig = detailsModalType
        ? detailsModalConfig[detailsModalType]
        : null

    const ratingSummarySections = getSummarySections({
        garantiasData,
        fatosRelevantesData: fatosData,
        offerData: ofertaData,
        handleClickEmission: handleClickSummaryModal,
        stateRating,
        stateDebtorEmission,
        statePriceProcess,
        lastBusinessState,
        stateIndexLiquidity,
        lastEventCreditEmissionState,
        lastEventCreditDebtorState,
    })

    return (
        <>
            <EmissionDetailsModal
                open={modalIsVisible}
                onClose={() => {
                    setModalIsVisible(false)
                    setModalType("")
                }}
                title={selectedModalConfig?.title ?? ""}
                sections={selectedModalConfig?.sections ?? []}
                empty={selectedModalConfig?.empty ?? false}
                emptyText={selectedModalConfig?.emptyText ?? ""}
            />

            <EmissionDetailsModal
                open={detailsModalVisible}
                onClose={() => {
                    setDetailsModalVisible(false)
                    setDetailsModalType("")
                }}
                title={selectedDetailsModalConfig?.title ?? ""}
                sections={selectedDetailsModalConfig?.sections ?? []}
                empty={selectedDetailsModalConfig?.empty ?? false}
                emptyText={selectedDetailsModalConfig?.emptyText ?? ""}
            />

            <Box>
                <Grid container rowSpacing={3}>
                    {ratingSummarySections.map((section) => (
                        <Grid
                            key={section.title}
                            item
                            {...section.gridProps}
                            sx={{
                                display: "flex",
                                ...(section.sx ?? {}),
                            }}
                        >
                            <SummarySection
                                title={section.title}
                                fields={section.fields}
                                empty={section.empty}
                                emptyText={section.emptyText}
                                onClick={section.onClick}
                                fieldGridProps={section.fieldGridProps}
                                loading={section.loading}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    )
}

EmissionSummarySections.propTypes = {
    emissaoDatalakeService: PropTypes.any,
    characterData: PropTypes.any,
    contraparteData: PropTypes.any,
    garantiasData: PropTypes.any,
    offerData: PropTypes.any,
    fatosRelevantesData: PropTypes.array,
    admin: PropTypes.bool,
}